import { useState } from 'react';
import { Link } from 'react-router';
import { GoogleLogin } from '@react-oauth/google';
import { FacebookLoginButton } from './FacebookLoginButton';
import { useAuth } from '../lib/auth';
import { authService } from '../lib/api-service';
import { Mail, KeyRound } from 'lucide-react';

interface LoginPromptModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function LoginPromptModal({ open, onClose, onSuccess }: LoginPromptModalProps) {
  const { login, loginWithGoogle, loginWithFacebook } = useAuth();
  const [error, setError] = useState('');
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [submittingPassword, setSubmittingPassword] = useState(false);

  if (!open) return null;

  async function handleEmailSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await authService.requestMagicLink(email);
      setEmailSent(true);
    } catch {
      setError('A apărut o eroare. Încearcă din nou.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="mx-4 w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          className="mb-2 text-center text-lg font-bold text-[var(--vivid-purple)]"
          style={{ fontFamily: 'var(--font-primary)' }}
        >
          Autentificare necesară
        </h2>
        <p
          className="mb-6 text-center text-sm text-muted-foreground"
          style={{ fontFamily: 'var(--font-secondary)' }}
        >
          Conectează-te pentru a te înscrie la această activitate
        </p>

        {error && (
          <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
        )}

        {emailSent ? (
          <div className="text-center">
            <Mail className="mx-auto mb-4 h-10 w-10 text-[var(--vivid-purple)]" />
            <p className="mb-2 text-sm font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
              Verifică-ți email-ul
            </p>
            <p className="text-sm text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
              Am trimis un link de conectare la {email}
            </p>
          </div>
        ) : showPasswordForm ? (
          <form onSubmit={async (e) => {
            e.preventDefault();
            setSubmittingPassword(true);
            setError('');
            try {
              await login(username, password);
              onSuccess();
            } catch {
              setError('Nume de utilizator sau parolă incorectă.');
            } finally {
              setSubmittingPassword(false);
            }
          }}>
            <div className="mb-4">
              <input
                type="text"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Utilizator"
                autoFocus
                className="w-full rounded-lg border px-3 py-2.5 text-sm outline-none transition-colors focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20"
                style={{ fontFamily: 'var(--font-secondary)' }}
              />
            </div>
            <div className="mb-4">
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Parolă"
                className="w-full rounded-lg border px-3 py-2.5 text-sm outline-none transition-colors focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20"
                style={{ fontFamily: 'var(--font-secondary)' }}
              />
            </div>
            <button
              type="submit"
              disabled={submittingPassword}
              className="w-full rounded-lg bg-[var(--vivid-purple)] py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[var(--vivid-purple)]/90 disabled:opacity-60"
              style={{ fontFamily: 'var(--font-primary)' }}
            >
              {submittingPassword ? 'Se autentifică...' : 'Autentificare'}
            </button>
            <div className="mt-3 flex items-center justify-between">
              <button
                type="button"
                onClick={() => setShowPasswordForm(false)}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                Înapoi
              </button>
              <Link
                to="/staff/parola-uitata"
                onClick={onClose}
                className="text-xs text-muted-foreground hover:text-foreground"
                style={{ fontFamily: 'var(--font-secondary)' }}
              >
                Ai uitat parola?
              </Link>
            </div>
          </form>
        ) : showEmailForm ? (
          <form onSubmit={handleEmailSubmit}>
            <div className="mb-4">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Adresa ta de email"
                autoFocus
                className="w-full rounded-lg border px-3 py-2.5 text-sm outline-none transition-colors focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20"
                style={{ fontFamily: 'var(--font-secondary)' }}
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-lg bg-[var(--vivid-purple)] py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[var(--vivid-purple)]/90 disabled:opacity-60"
              style={{ fontFamily: 'var(--font-primary)' }}
            >
              {submitting ? 'Se trimite...' : 'Trimite link de conectare'}
            </button>
            <button
              type="button"
              onClick={() => setShowEmailForm(false)}
              className="mt-2 w-full text-center text-xs text-muted-foreground hover:text-foreground"
            >
              Înapoi
            </button>
          </form>
        ) : (
          <div className="space-y-3">
            <div className="flex justify-center">
              <GoogleLogin
                onSuccess={async (response) => {
                  if (response.credential) {
                    setError('');
                    try {
                      await loginWithGoogle(response.credential);
                      onSuccess();
                    } catch {
                      setError('Autentificarea cu Google a eșuat.');
                    }
                  }
                }}
                onError={() => setError('Autentificarea cu Google a eșuat.')}
                text="signin_with"
                shape="rectangular"
                width="308"
              />
            </div>
            <div className="mx-auto" style={{ maxWidth: 308 }}>
              <FacebookLoginButton
                onSuccess={async (accessToken) => {
                  setError('');
                  try {
                    await loginWithFacebook(accessToken);
                    onSuccess();
                  } catch {
                    setError('Autentificarea cu Facebook a eșuat.');
                  }
                }}
                onError={() => setError('Autentificarea cu Facebook a eșuat.')}
              />
            </div>
            <div className="mx-auto" style={{ maxWidth: 308 }}>
              <button
                type="button"
                onClick={() => setShowEmailForm(true)}
                className="flex w-full items-center justify-center gap-2 rounded-md border bg-white px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-gray-50"
                style={{ fontFamily: 'var(--font-primary)' }}
              >
                <Mail className="h-5 w-5" />
                Conectează-te cu email
              </button>
            </div>
            <div className="mx-auto" style={{ maxWidth: 308 }}>
              <button
                type="button"
                onClick={() => setShowPasswordForm(true)}
                className="flex w-full items-center justify-center gap-2 rounded-md border bg-white px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-gray-50"
                style={{ fontFamily: 'var(--font-primary)' }}
              >
                <KeyRound className="h-5 w-5" />
                Conectează-te cu parolă
              </button>
            </div>
          </div>
        )}

        <button
          onClick={onClose}
          className="mt-4 w-full text-center text-sm text-muted-foreground hover:text-foreground"
          style={{ fontFamily: 'var(--font-secondary)' }}
        >
          Anulează
        </button>
      </div>
    </div>
  );
}
